import { ActionCategory, ActionDef, FlowNode, JourneyCategory, TriggerDef } from "./types";

// ─── Trigger & action catalog — now backend-driven ─────────────────────────
// GET /v1/bot/triggers and GET /v1/bot/actions are live (see use-catalog.ts,
// which fetches and calls the setters below once on mount from both
// create-journey-modal.tsx and builder-page.tsx). These start empty and are
// populated at runtime; components that render them directly (add-panel.tsx,
// create-journey-modal.tsx) take a `loading` flag from useJourneyCatalog()
// so they don't render "no results" off the empty starting state, and
// imperative helpers below (findActionDef, makeFlowNode, ...) just read
// whatever's currently loaded — they're only ever called from user
// interactions that happen after the palette they came from has rendered,
// so by then the catalog is already populated.
export let JOURNEY_CATEGORIES: JourneyCategory[] = [];
export let ACTION_CATEGORIES: ActionCategory[] = [];
export let QUICK_ACTIONS: ActionDef[] = [];
export let ALL_ACTIONS: ActionDef[] = [];

export function setJourneyCategories(categories: JourneyCategory[]) {
  JOURNEY_CATEGORIES = categories;
}

export function setActionCatalog(categories: ActionCategory[], quickActions: ActionDef[]) {
  ACTION_CATEGORIES = categories;
  QUICK_ACTIONS = quickActions;
  ALL_ACTIONS = [...quickActions, ...categories.flatMap((c) => c.nodes)];
}

export const findActionDef = (type: string): ActionDef | undefined => ALL_ACTIONS.find((a) => a.id === type);

// Real trigger description straight from GET /v1/bot/triggers, for the
// config panel/canvas header subtitle — no hardcoded per-trigger copy.
export const findTriggerDef = (id: string): TriggerDef | undefined => {
  for (const category of JOURNEY_CATEGORIES) {
    const trigger = category.triggers.find((t) => t.id === id);
    if (trigger) return trigger;
  }
  return undefined;
};

// Picks the default trigger for "start from scratch" — explicitly
// "Signup Completed" (the most natural default for a new customer journey),
// wherever it happens to live in the fetched catalog, regardless of its
// status (the real catalog currently marks it "planned" — still the right
// default to land on, just not yet a trigger a real bot can go live on).
// Falls back to the first genuinely available trigger, then the first
// trigger at all, only if "signup_completed" isn't in the catalog at all.
export function findDefaultTrigger(): { category: JourneyCategory; trigger: TriggerDef } | undefined {
  for (const category of JOURNEY_CATEGORIES) {
    const trigger = category.triggers.find((t) => t.id === "signup_completed");
    if (trigger) return { category, trigger };
  }
  for (const category of JOURNEY_CATEGORIES) {
    const trigger = category.triggers.find((t) => t.status !== "planned");
    if (trigger) return { category, trigger };
  }
  for (const category of JOURNEY_CATEGORIES) {
    if (category.triggers[0]) return { category, trigger: category.triggers[0] };
  }
  return undefined;
}

// Node types whose continuation branches instead of running linear. Backend
// ids for these are stable regardless of which category they're grouped
// under, so this stays a fixed set rather than being derived from the fetch.
// "holdout" and "ai_moderate" added per direct ask — confirmed live against
// salary_day_loan's own real edges, both carry a genuine diverging true/false
// pair (held-out-of-experiment vs. continues; passed-moderation vs.
// flagged-fallback) despite neither being a "condition"/"branch" node type.
// Importing one without recognizing it as branching meant journey-flow-
// import.ts's buildNode silently followed only the first edge it found and
// dropped the entire other side — which, for salary_day_loan's Holdout node,
// was the side carrying AI Generate, AI Moderate and both Send WhatsApp
// variants: exactly the "AI Generate never shows up" bug this fixes. A
// freshly-dragged Holdout/AI Moderate node from the palette now also starts
// with a TRUE/FALSE pair instead of a single linear continuation (see
// makeFlowNode below) — a real behavior change for manually-built canvases,
// but the correct one: both nodes really do fork in the real backend schema.
export const BRANCHING_TYPES = new Set(["condition", "branch", "holdout", "ai_moderate"]);

// Sentinel optionId for a grouped Condition node's implicit "nothing matched"
// branch (Figma redesign, 20 Oct) — shared by catalog.ts/flow-model.ts/
// flow-canvas.tsx/config-panel.tsx so all four agree on one literal instead
// of each hand-rolling their own magic string.
export const ON_FAILURE_BRANCH_ID = "__on_failure__";

// ─── Default config per node type ──────────────────────────────────────────
export const getDefaultNodeConfig = (type: string): Record<string, any> => {
  switch (type) {
    case "fetch_profile":
      // `fetch`/`fetchLabels` are seeded once GET /v1/bot/profile-attributes
      // resolves for this journey (config-panel.tsx) — left unset here since
      // the real default list is journey-scoped, not a fixed shape.
      return {
        profileSource: "",
        findCustomerBy: "Customer ID",
        value: "",
        // UI-only placeholder (Figma redesign, 20 Oct) — no backend
        // failure-policy field confirmed live yet for this node, same
        // "UI-only pending backend" precedent as ai_moderate's checks below.
        onFailure: "retry_then_stop",
      };
    case "ai_generate":
      return {
        contentName: "",
        prompt: "Generate a personalised message for",
        channel: "WhatsApp",
        channels: ["WhatsApp"],
        tone: "Friendly",
        length: "Short (less than 200 char)",
        keepWithinLimits: true,
        includeFirstName: true,
        includeCta: true,
        delivery: "immediately",
        sendAfterValue: 1,
        sendAfterUnit: "min",
        title: "",
        description: "",
        ctaText1: "",
        ctaLink1: "",
        // Additive fields for the Figma redesign (20 Oct) — "Required
        // variables" and "Constraints" are UI-only (no confirmed backend
        // schema for either yet), same precedent as ai_moderate's checks.
        contentMode: "fill_approved_template",
        fallbackTemplateId: "",
        requiredVariables: [] as string[],
        constraints: [] as string[],
        // Required for this node to publish (confirmed: the six shipped
        // journeys all carry one, e.g. salary_day_loan's own AI Generate node
        // has "banking_offer_personalise_v1" — a fresh card otherwise 422s
        // with "'<label>' has no prompt template"). No backend catalog of
        // valid ids is confirmed live, so this is a placeholder default
        // rather than a real id — flag to backend if it's rejected; the field
        // below the Prompt box lets the operator override it either way.
        prompt_template_id: "general_personalise_v1",
      };
    case "send_message":
    case "send_email":
    case "send_whatsapp":
    case "send_rcs":
    case "send_sms":
    case "send_push":
      return {
        channel: type === "send_email" ? "Email" : type === "send_whatsapp" ? "WhatsApp" : type === "send_rcs" ? "RCS" : type === "send_sms" ? "SMS" : "WhatsApp",
        template: "",
        delivery: "immediately",
        sendAfterValue: 1,
        sendAfterUnit: "min",
        title: "",
        description: "",
        ctaText1: "",
        ctaLink1: "",
        messageBody: "",
        shortenUrl: true,
      };
    case "make_voice_call":
      return { script: "" };
    // No config-panel form for these three yet — left empty rather than
    // pre-seeded with a blank entry, so the canvas card's fallback
    // description shows (see flow-canvas.tsx) instead of a misleading count.
    case "send_menu":
      return { replyText: "", chips: [] };
    case "send_carousel":
      return { cards: [] };
    case "segment":
      return {
        segmentName: "",
        conditions: [{ attribute: "Order value", comparator: "> 3" }],
      };
    case "update_profile":
      return { attribute: "", value: "" };
    case "assign_agent":
      return { agent: "Sales agent", agentAction: "Fetch", fetchName: true, fetchBusinessInfo: true, fetchReadyToOnboard: true };
    case "assign_people":
      // `assignerId`/`message` are seeded once GET /v1/bot/assigners resolves
      // (config-panel.tsx) — left unset here since the real assigner list and
      // its default follow-up message come from the backend, not a fixed set.
      return { assignerId: "", message: "", delivery: "immediately", sendAfterValue: 1, sendAfterUnit: "min" };
    case "create_crm_task":
      // `queue` is one of GET /v1/bot/assigners' kind:"queue" entries
      // (config-panel.tsx) — "rm_call_queue" matches the real
      // loan_abandonment_recovery flow's own default.
      return { queue: "rm_call_queue", priority: "normal" };
    case "create_support_ticket":
      return { priority: "high", notes: "" };
    case "create_lead":
      return { source: "Website Form" };
    case "escalate":
      return { reason: "" };
    case "fallback":
      return { message: "" };
    case "condition":
      return {
        mode: "rule", // "rule" (If/Else, AND/OR) | "boolean" (This or That)
        conditions: [{ attribute: "Previous Purchase Count", operator: "Equal", value: "0" }],
        matchLogic: "all", // "all" | "any"
        elseWhen: "",
        booleanCheck: "Customer purchase history",
        booleanTrueOperator: "Equal",
        booleanTrueValue: "0",
        booleanFalseOperator: "Greater than",
        booleanFalseValue: "0",
        trueBehavior: "end",
        falseBehavior: "continue",
        // New Figma shape (20 Oct) — a freshly-created condition node starts
        // here. An existing node stays on the fields above (old flat
        // rule/boolean shape) until the operator edits it through the new
        // groups UI — see isGroupedCondition/migrateConditionConfig,
        // flow-model.ts — so this is purely additive, not a replacement.
        conditionGroups: [{ id: "group_0", name: "Condition 1", matchLogic: "all", rows: [{ id: "row_0", attribute: "", operator: "", value: "" }] }],
        endWorkflowOnFailure: true,
      };
    case "branch":
      // Real shape, confirmed against GET /v1/bot/journeys/fraud_anomaly_alert
      // (`{mode: "parallel", timeout_ms}` fanning out to two real child
      // nodes, e.g. Send SMS + Send Push both firing — no per-branch
      // condition at all, unlike Condition's true/false). `mode` is the only
      // one surfaced in config-panel.tsx's "Branch Type" radios for now.
      // Both sides default open (not capped with an END badge) since a
      // parallel branch's whole point is two real paths to build into.
      return { mode: "parallel", trueBehavior: "continue", falseBehavior: "continue" };
    // "wait_until_event" is a real GET /v1/bot/actions catalog id, but its
    // config panel is identical to the quick-action "wait" — pause for a
    // duration, then continue or end (confirmed against the real design).
    case "wait":
    case "wait_until_event":
      return { value: 2, unit: "hrs", thenBehavior: "continue" };
    case "ai_moderate":
      // Real GET /v1/bot/journeys/dormancy_reactivation shows ai_moderate
      // with an empty config live — backend hasn't published a real checks
      // schema yet, so these 5 labels are a UI-only starting point (matches
      // the reference design) pending backend confirmation.
      return { checks: ["regulatory_claims", "financial_promises", "unsupported_claims", "restricted_language"] };
    case "ai_classify":
      // Same: real backend data for this node type is `config: {}` today —
      // tiers/attribute below are a UI-only starting point, not a confirmed
      // backend contract yet.
      return {
        classifyAttribute: "",
        tiers: [
          { id: "tier_1", label: "High", operator: "above", value: "" },
          { id: "tier_2", label: "Medium", operator: "between", value: "", value2: "" },
          { id: "tier_3", label: "Low", operator: "below", value: "" },
        ],
      };
    case "ai_decide":
      return {
        decideAttribute: "",
        options: [
          { id: "opt_1", label: "", description: "" },
          { id: "opt_2", label: "", description: "" },
        ],
      };
    case "ai_format":
    case "ai_summarize":
      return { instruction: "" };
    default:
      return {};
  }
};

let uid = 0;
export const makeNodeId = () => `node_${Date.now().toString(36)}_${(uid++).toString(36)}`;

export const makeFlowNode = (type: string): FlowNode => {
  const def = findActionDef(type);
  const node: FlowNode = {
    id: makeNodeId(),
    type,
    category: def?.category ?? "data",
    label: def?.nodeLabel ?? type,
    config: getDefaultNodeConfig(type),
    next: null,
  };
  // Condition is checked ahead of the generic BRANCHING_TYPES case — a fresh
  // condition node now starts on the new named-group shape (optionBranches,
  // like AI Decide), not the old fixed true/false pair. "branch" is
  // unaffected and still gets `.branches` below.
  if (type === "condition") {
    const groups = (node.config.conditionGroups as { id: string }[]) || [];
    node.optionBranches = [
      ...groups.map((g) => ({ optionId: g.id, behavior: "continue" as const, next: null })),
      { optionId: ON_FAILURE_BRANCH_ID, behavior: node.config.endWorkflowOnFailure ? ("end" as const) : ("continue" as const), next: null },
    ];
  } else if (BRANCHING_TYPES.has(type)) {
    node.branches = {
      true: { behavior: node.config.trueBehavior === "continue" ? "continue" : "end", next: null },
      false: { behavior: node.config.falseBehavior === "continue" ? "continue" : "end", next: null },
    };
  }
  if (type === "ai_decide") {
    node.optionBranches = (node.config.options as { id: string }[]).map((opt) => ({ optionId: opt.id, behavior: "continue", next: null }));
  }
  return node;
};

// The default scaffold seeded right after any trigger is created — per the
// explicit ask, Fetch Profile and AI Generate always chain onto a trigger.
export const makeDefaultTriggerChain = (): FlowNode => {
  const fetchProfile = makeFlowNode("fetch_profile");
  const aiGenerate = makeFlowNode("ai_generate");
  fetchProfile.next = aiGenerate;
  return fetchProfile;
};
